import { COGNITO } from '@/config/constants'
import type { Entitlements } from '@/types/schemas/api'

// ─── Plan Tiers ───

/**
 * The five canonical tiers. Mirrors the backend plan model: `PLAN_RANK` in
 * `apps/edge-user-api/edge_user_api/services/stripe_mapping.py` and
 * `plan_permissions` in edge-financial-api. Every `Record<PlanTier, …>` table
 * below must carry all five; `plan-constraint-utils.unit.test.ts` enforces it.
 */
export type PlanTier = 'base' | 'basic' | 'pro' | 'pro_plus' | 'ultimate'

/** Canonical tier ids in rank order, lowest first. */
export const CANONICAL_PLAN_TIERS: readonly PlanTier[] = [
  'base',
  'basic',
  'pro',
  'pro_plus',
  'ultimate',
]

export const TIER_ORDER: Record<PlanTier, number> = {
  base: 0,
  basic: 1,
  pro: 2,
  pro_plus: 3,
  ultimate: 4,
}

/**
 * Feature identifiers that may be gated by subscription plan.
 */
export type Feature =
  | 'scanner'
  | 'multi-scanner'
  | 'multi-chart'
  | 'exports'
  | 'history'
  | 'watchlist'
  | 'expressions'
  | 'advanced-filters'
  | 'history-custom-layouts'
  | 'history-unlimited-layouts'
  | 'backtest'
  | 'ai-analyze'
  | 'ai-build'
  | 'deep-research'
  | 'theme-tracker'
  | 'notebook'
  | 'history-market-stats'
  | 'history-sankey'
  | 'keystats-dilution'
  | 'ticker-stats-custom'

/**
 * Minimum plan tier required for each feature — the OFFLINE fallback for the
 * server's `features` list (see the DEFAULT_* note below). A feature is allowed
 * when the payload names it; only until the payload lands does this table decide.
 *
 * `plan-constraint-utils.unit.test.ts` asserts each entry equals the lowest tier
 * whose served `features` list contains it, so the two cannot disagree.
 */
export const FEATURE_REQUIREMENTS: Record<Feature, PlanTier> = {
  scanner: 'base',
  'multi-scanner': 'pro',
  'multi-chart': 'pro',
  // Pro+ per the 2026-09-09 entitlements audit (D2). The old 'pro' was never read
  // by any caller and disagreed with the export route, which 403s below pro_plus.
  exports: 'pro_plus',
  history: 'basic',
  watchlist: 'base',
  expressions: 'basic',
  'advanced-filters': 'pro',
  'history-custom-layouts': 'pro',
  'history-unlimited-layouts': 'pro_plus',
  backtest: 'pro',
  // Market answers (2026-10-15, "No more modes") and changes made by the
  // assistant (2026-10-16, Build removed) are on every plan with AI, and since
  // 2026-10-16 every plan has AI (Base: "the small taste"), limited by its
  // budget. The AI layout generator in History reads `ai-build`.
  'ai-analyze': 'base',
  'ai-build': 'base',
  // Deep Research reports (research runs). Answers on the research page need
  // only `ai-analyze`; the page's Deep report switch reads this (2026-10-16).
  'deep-research': 'pro',
  // Panels the 2026-09-09 sheet puts behind Pro. The server has granted these
  // feature ids since step 2; step 6 is the client finally reading them.
  'theme-tracker': 'pro',
  notebook: 'pro',
  'history-market-stats': 'pro',
  'history-sankey': 'pro',
  'keystats-dilution': 'pro',
  'ticker-stats-custom': 'pro',
}

/**
 * Human names for the gated features, used by upgrade copy so a pitch never
 * has to spell out an id. Every `Feature` must appear: the exhaustive Record
 * makes adding a gate without naming it a type error.
 */
export const FEATURE_LABELS: Record<Feature, string> = {
  scanner: 'Scanner',
  'multi-scanner': 'Multi-Scanner',
  'multi-chart': 'Chart Scanner',
  exports: 'Data exports',
  history: 'History',
  watchlist: 'Watchlists',
  expressions: 'Custom expressions',
  'advanced-filters': 'Advanced filters',
  'history-custom-layouts': 'Custom layouts',
  'history-unlimited-layouts': 'Unlimited custom layouts',
  backtest: 'Backtester',
  'ai-analyze': 'AI Analyze',
  'ai-build': 'AI layout generator',
  'deep-research': 'Deep Research reports',
  'theme-tracker': 'Theme Tracker',
  notebook: 'Notebook',
  'history-market-stats': 'Market Stats',
  'history-sankey': 'Flow Graph',
  'keystats-dilution': 'Key Stats dilution',
  'ticker-stats-custom': 'Custom Ticker Stats',
}

/** Plan names as sold. Mirrors PLAN_DISPLAY_NAMES in the backend's stripe_mapping. */
export const PLAN_LABELS: Record<PlanTier, string> = {
  base: 'Base',
  basic: 'Basic',
  pro: 'Pro',
  pro_plus: 'Pro+',
  ultimate: 'Ultimate',
}

// ─── Offline defaults ───
//
// The DEFAULT_* tables below are NOT the source of truth. The server owns every
// plan limit in `apps/edge-user-api/edge_user_api/services/entitlements.py` and
// serves it from `GET /v2/users/me/entitlements`; these are what the app shows in
// the window before that payload lands, and in mock-auth dev mode where nothing
// is fetched. `plan-constraint-utils.unit.test.ts` reads the backend's JSON
// snapshot and fails if the two drift. Changing a limit for real is a backend edit.

/** Max custom layouts per tier. Offline default. */
export const DEFAULT_MAX_CUSTOM_LAYOUTS: Record<PlanTier, number> = {
  base: 0,
  basic: 0,
  // One, matching the legacy platform's single custom layout (2026-09-25).
  // Presets are not custom layouts and never count against this.
  pro: 1,
  pro_plus: Infinity,
  ultimate: Infinity,
}

/** Max scanner rows per tier. Offline default. */
export const DEFAULT_SCANNER_ROW_LIMIT: Record<PlanTier, number> = {
  base: 50,
  basic: 50,
  pro: 75,
  pro_plus: 100,
  ultimate: 150,
}

/**
 * Scanner poll interval (ms) per tier. Base polls slowly; every paid tier is 5 s.
 * Offline default.
 */
export const DEFAULT_SCANNER_POLL_INTERVAL: Record<PlanTier, number> = {
  base: 10_000,
  basic: 10_000,
  pro: 5_000,
  pro_plus: 5_000,
  ultimate: 5_000,
}

/**
 * Scanner panels allowed in one layout, per tier. `Infinity` is unlimited.
 * Offline default for the served `scanner_panels_per_layout_max`.
 */
export const DEFAULT_SCANNER_PANELS_PER_LAYOUT: Record<PlanTier, number> = {
  base: 1,
  basic: 2,
  pro: Infinity,
  pro_plus: Infinity,
  ultimate: Infinity,
}

/**
 * Max user-defined expressions per namespace per tier.
 * Basic users see one as a sample / upgrade promo; Pro and up get the full set.
 * (Mirrors the legacy frontend's `expressions_max_expressions` constraint.)
 * Offline default.
 */
export const DEFAULT_MAX_EXPRESSIONS_PER_TIER: Record<PlanTier, number> = {
  base: 0,
  basic: 1,
  pro: 30,
  pro_plus: 30,
  ultimate: 30,
}

/**
 * Ticker days one batch backtest may run on, per tier. Base and Basic cannot
 * backtest, so the cap does not apply (the server sends null). Offline default
 * for the served `backtest_tickers_per_run`.
 */
export const DEFAULT_BACKTEST_TICKERS_PER_RUN: Record<PlanTier, number> = {
  base: Infinity,
  basic: Infinity,
  pro: 10,
  pro_plus: 500,
  ultimate: 6000,
}

/**
 * How many days back a batch backtest may reach, per tier. Not applicable on
 * Base and Basic, as above. Offline default for the served
 * `backtest_history_days`.
 */
export const DEFAULT_BACKTEST_HISTORY_DAYS: Record<PlanTier, number> = {
  base: Infinity,
  basic: Infinity,
  pro: 7,
  pro_plus: 93,
  ultimate: 5110,
}

/** A batch backtest's plan caps. `Infinity` means none. */
export interface BacktestRunLimits {
  tickersPerRun: number
  historyDays: number
}

/**
 * The plan's batch backtest caps: the served payload once it has landed, the
 * offline tables until then and under a tier preview (Chris, 2026-11-02: the
 * default Stock Selection fits the plan, so a new strategy runs as it opens).
 */
export function resolveBacktestRunLimits(
  entitlements: Pick<Entitlements, 'backtest_tickers_per_run' | 'backtest_history_days'> | null,
  planTier: PlanTier,
): BacktestRunLimits {
  const served = (value: number | null | undefined, fallback: number) =>
    value === undefined ? fallback : value === null ? Infinity : value
  return {
    tickersPerRun: served(
      entitlements?.backtest_tickers_per_run,
      DEFAULT_BACKTEST_TICKERS_PER_RUN[planTier],
    ),
    historyDays: served(
      entitlements?.backtest_history_days,
      DEFAULT_BACKTEST_HISTORY_DAYS[planTier],
    ),
  }
}

// ─── Tier Derivation ───

/** Detect whether we're running in dev/mock mode (no Cognito configured). */
export const IS_MOCK_AUTH = !COGNITO.userPoolId

/** Unknown plan names already warned about, so the log fires once per name. */
const warnedUnknownPlans = new Set<string>()

/**
 * Derive plan tier from the canonical `plan_level` or, for legacy rows that lack
 * one, the display name.
 *
 * Exact canonical ids win first; the fuzzy name matches are the legacy fallback.
 * A name we cannot place still resolves to `basic` (refusing outright would lock a
 * paying customer out of the app), but it is logged rather than silently swallowed
 * so a tier the frontend has not learned about is visible in the console.
 *
 * In dev mode, defaults to `ultimate` so nothing is gated.
 */
export function derivePlanTier(planName: string | null | undefined): PlanTier {
  if (IS_MOCK_AUTH) return 'ultimate'
  if (!planName) return 'basic'

  // Canonical plan ids, matched exactly. This is the normal path: plan_level is
  // written by the backend's extract_plan_level.
  if ((CANONICAL_PLAN_TIERS as readonly string[]).includes(planName)) {
    return planName as PlanTier
  }

  // Legacy fallback: match the display name ("EDGE Pro+", "EDGE Ultimate (FREE)").
  // Order matters, the longer stems must be tested first.
  const normalized = planName.toLowerCase().replace(/[\s_-]+/g, '')
  if (normalized.includes('ultimate')) return 'ultimate'
  if (normalized.includes('proplus') || normalized.includes('pro+')) return 'pro_plus'
  if (normalized.includes('pro')) return 'pro'
  if (normalized.includes('basic')) return 'basic'
  if (normalized.includes('base')) return 'base'

  if (!warnedUnknownPlans.has(planName)) {
    warnedUnknownPlans.add(planName)
    console.warn(
      `[plan] Unrecognised plan "${planName}"; falling back to the basic tier. ` +
        `Add it to PlanTier in plan-constraint-utils.ts if it is a real tier.`,
    )
  }
  return 'basic'
}

/** The subscription fields tier resolution reads. Loose so tests can pass partial rows. */
interface TierSubscription {
  subscriptionStatus?: string | null
  planDetails?: { plan_level?: string | null; name?: string | null } | null
}

/** Statuses that still grant the plan (past_due sits in its grace window). */
const LIVE_SUBSCRIPTION_STATUSES = new Set(['active', 'trialing', 'past_due'])

/**
 * The subscription the plan comes from: the highest-tier row still live, not
 * `subscriptions[0]`, which can be a canceled row left ahead of the current one.
 */
export function pickLiveSubscription<S extends TierSubscription>(
  subscriptions: readonly S[] | null | undefined,
): S | null {
  let best: S | null = null
  let bestRank = -1
  for (const sub of subscriptions ?? []) {
    if (!LIVE_SUBSCRIPTION_STATUSES.has(sub.subscriptionStatus ?? '')) continue
    const rank = TIER_ORDER[derivePlanTier(sub.planDetails?.plan_level ?? sub.planDetails?.name)]
    if (rank > bestRank) {
      best = sub
      bestRank = rank
    }
  }
  return best
}

/**
 * THE plan tier, for every gate in the app. The dev override wins, then the
 * plan_level the entitlements payload was resolved for (the same row every
 * limit and `hasAccess` read), then the live subscription until that lands.
 */
export function resolvePlanTier({
  override,
  servedPlanLevel,
  subscriptions,
}: {
  override?: PlanTier | null
  servedPlanLevel?: string | null
  subscriptions?: readonly TierSubscription[] | null
}): PlanTier {
  if (override) return override
  if (servedPlanLevel) return derivePlanTier(servedPlanLevel)
  const live = pickLiveSubscription(subscriptions)
  return derivePlanTier(live?.planDetails?.plan_level ?? live?.planDetails?.name)
}

/** Check whether `userTier` meets the `requiredTier`. */
export function tierMeetsRequirement(userTier: PlanTier, requiredTier: PlanTier): boolean {
  return TIER_ORDER[userTier] >= TIER_ORDER[requiredTier]
}
